import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import api, { fetchDealsList } from '../../api';
import { pushEvent, pushFbEvent } from '../../utilities/analytics';
import { LOAD_SEARCH_RESULTS_TO_DISPLAY_EVENT, LOAD_OFFERS_EVENT, NO_OFFERS_EVENT } from '../../constants/analyticEvents';
import { abortControllerManager } from '../../services/AbortControllerManager';
import { sendSearchPerformed } from '../../utilities/sendSearchPerformed';

const initialState = {
  pending: false,
  error: false,
  saveDirectly: false,
  deals: [],
  pagination: {},
  timestamp: null,
};

const callEvents = (data, userProfile) => {
  let minOfferPrice = null;
  if (data.offers && data.offers.length) {
    const minOffer = data.offers.reduce((prev, curr) => prev.offer.price < curr.offer.price ? prev : curr);
    if (minOffer && minOffer.offer) {
      const { capacity_from_filter } = minOffer.offer;
      let kids = capacity_from_filter.child;
      const babies = capacity_from_filter.childAges.filter((kid) => kid < 2).length || 0;
      kids = kids - babies; // calculate price per person without babies
      const minPricePerPerson = Math.ceil(minOffer.offer.price / (capacity_from_filter.adult + kids));
      minOfferPrice = minPricePerPerson;
      window.minOfferPricePP = minPricePerPerson;
    }
  }
  sendSearchPerformed(minOfferPrice);
  if (data.offers.length === 0) {
    return pushEvent(NO_OFFERS_EVENT);
  }
  pushEvent(LOAD_OFFERS_EVENT, { offers: data.offers, userProfile: userProfile?.searchProfileData });
  if (data.facebook_product_id) {
    pushFbEvent(data.facebook_product_id);
  }
};

export const prefetchDeals = createAsyncThunk(
  'prefetchDeals',
  async (params, { getState }) => {
    const { request, signal } = params;
    const { onboarding } = getState();
    const { style } = onboarding?.formV2
    const startTime = Date.now();
    const userResponse = await api.getUserProfile(request, style, signal);
    const userProfileData = userResponse?.data;

    const response = await fetchDealsList(request, request.specificHotel?.hotel_id, userProfileData, signal, startTime, 'fetchDealsList');
    callEvents(response, userProfileData);

    return response
  }
);

export const prefetchDealsSlice = createSlice({
  name: 'prefetchDeals',
  initialState,
  reducers: {
    setSavePrefetchDirectly: (state, action) => {
      state.saveDirectly = action.payload;
      state.timestamp = Date.now();
    },
    clearPrefetchDeals: (state) => {
      state.pending = false;
      state.error = false;
      state.saveDirectly = false;
      state.deals = [];
      state.pagination = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase('filters/clearClick', (state) => {
        abortControllerManager.abortRequest('warmup-deals');
        state.pending = false;
        state.error = false;
        state.saveDirectly = false;
        state.deals = [];
        state.pagination = {};
      })
      .addCase(prefetchDeals.pending, (state) => {
        state.pending = true;
        state.error = false;
        state.saveDirectly = false;
        state.deals = [];
        state.pagination = {};
      })
      .addCase(prefetchDeals.fulfilled, (state, action) => {
        const currentState = current(state);
        const { offers, pagination } = action.payload;

        if (currentState.saveDirectly) {
          const loadDurationTime = Date.now() - currentState.timestamp;
          pushEvent(LOAD_SEARCH_RESULTS_TO_DISPLAY_EVENT, {
            loadDurationTime,
            responseData: {
              data: action.payload
            },
          });
        }

        return {
          ...state,
          deals: offers,
          pagination: pagination,
          pending: false,
          error: false,
        }
      })
      .addCase(prefetchDeals.rejected, (state) => {
        state.pending = false;
        state.error = true;
        state.saveDirectly = false;
      })
  }
});

export const { setSavePrefetchDirectly, clearPrefetchDeals } = prefetchDealsSlice.actions;

export default prefetchDealsSlice.reducer;


